import { createContext } from "react";

import { ArticleCardData } from "@baseComponents/ArticleCard";
import { Post } from "@lib/blog";

export interface BlogInterfaceData {
  slug: string;
  img: string;
  alt: string;
  title: string;
  desc: string;
  date: string;
  ttr: number;
  author: string;
}

export class BlogContextInterface {
  private posts: BlogInterfaceData[];

  constructor(posts: BlogInterfaceData[]) {
    this.posts = posts.toSorted(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }

  static postToBlogInterfaceData(
    post: Post,
    locale: string,
  ): BlogInterfaceData {
    const content = post.localizedContent[locale];

    return {
      slug: post.shortName,
      img: content.miniImage.src,
      alt: content.miniImage.alt,
      title: content.title,
      desc: content.description,
      date: content.date,
      ttr: BlogContextInterface.countTimeToRead(content.content),
      author: content.author,
    };
  }

  private static countTimeToRead(str: string): number {
    const words = str.split(/\s+/).length;
    return Math.ceil(words / 200);
  }

  private static dataToArticleCard(data: BlogInterfaceData): ArticleCardData {
    return {
      image: {
        src: data.img,
        alt: data.alt,
      },
      title: data.title,
      description: data.desc,
      date: new Date(data.date),
      timeToRead: data.ttr,
      author: data.author,
      href: `/blogs/${data.slug}`,
    };
  }

  getSortedArticleCards(): ArticleCardData[] {
    return this.posts.map(BlogContextInterface.dataToArticleCard);
  }

  getArticleCardByName(name: string): ArticleCardData {
    const post = this.posts.find(post => post.slug === name);

    if (!post) {
      throw new Error(`Post with name ${name} not found`);
    }

    return BlogContextInterface.dataToArticleCard(post);
  }
}

export const BlogContext = createContext<BlogContextInterface>(
  new BlogContextInterface([]),
);
